import { inject } from "@angular/core";
import { CanActivateFn, CanMatchFn, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, filter, map, take } from "rxjs";
import { landingUrlFor } from "../services/landing-policy";
import { sessionFeature } from "../store/session.reducer";
import { UserRole } from "../models/session.models";
import { PostLoginRedirectService } from "../services/post-login-redirect.service";

function settledSession() {
    const store = inject(Store);
    return combineLatest([
        store.select(sessionFeature.selectIsSettled),
        store
            .select(sessionFeature.selectUser)
            .pipe(map((user) => (user?.isGuest ? null : user))),
    ]).pipe(
        filter(([settled]) => settled),
        take(1)
    );
}

export const anonymousOnlyGuard: CanActivateFn = () => {
    const router = inject(Router);
    return settledSession().pipe(
        map(([, user]) =>
            user ? router.createUrlTree([landingUrlFor(user)]) : true
        )
    );
};

export const authenticatedGuard: CanActivateFn = (_route, state) => {
    const router = inject(Router);
    const redirects = inject(PostLoginRedirectService);
    return settledSession().pipe(
        map(([, user]) => {
            if (user) return true;
            const redirectUrl = redirects.remember(state.url);
            return router.createUrlTree(
                ["/login"],
                redirectUrl ? { queryParams: { redirectUrl } } : undefined
            );
        })
    );
};

/** Selects an authenticated route variant without redirecting anonymous users. */
export const authenticatedMatchGuard: CanMatchFn = () =>
    settledSession().pipe(map(([, user]) => Boolean(user)));

export function roleMatchGuard(role: UserRole): CanMatchFn {
    return () => settledSession().pipe(map(([, user]) => user?.role === role));
}

export function roleGuard(role: UserRole): CanActivateFn {
    return (_route, state) => {
        const router = inject(Router);
        const redirects = inject(PostLoginRedirectService);
        return settledSession().pipe(
            map(([, user]) => {
                if (!user) {
                    const redirectUrl = redirects.remember(state.url);
                    return router.createUrlTree(
                        ["/login"],
                        redirectUrl
                            ? { queryParams: { redirectUrl } }
                            : undefined
                    );
                }
                return user.role === role
                    ? true
                    : router.createUrlTree([landingUrlFor(user)]);
            })
        );
    };
}
